import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, BookOpen, GitBranch, MessageSquare, PhoneCall, History, Settings, CreditCard, ShieldCheck, LogOut, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import LanguageToggle from "./LanguageToggle";
import { BrandMark } from "./Logo";

const items = [
  { to: "/app/dashboard", icon: LayoutDashboard, key: "dashboard", testid: "side-dashboard" },
  { to: "/app/knowledge", icon: BookOpen, key: "kb", testid: "side-kb" },
  { to: "/app/escalations", icon: GitBranch, key: "rules", testid: "side-rules" },
  { to: "/app/demo", icon: MessageSquare, key: "demo", testid: "side-demo" },
  { to: "/app/call-demo", icon: PhoneCall, key: "call_demo", testid: "side-call-demo" },
  { to: "/app/sip", icon: Phone, key: "sip", testid: "side-sip" },
  { to: "/app/calls", icon: History, key: "calls", testid: "side-calls" },
  { to: "/app/settings", icon: Settings, key: "settings", testid: "side-settings" },
  { to: "/app/billing", icon: CreditCard, key: "billing", testid: "side-billing" },
];

export default function AppShell({ children }) {
  const { t } = useI18n();
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => { await logout(); navigate("/"); };

  const link = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition border-l-2 ${isActive ? "bg-primary/10 text-magenta border-magenta" : "text-slate-400 hover:text-white border-transparent hover:bg-white/[0.03]"}`;

  return (
    <div className="min-h-screen flex bg-bg">
      <aside className="hidden lg:flex flex-col w-64 bg-bg border-r border-white/5 sticky top-0 h-screen">
        <Link to="/" className="px-6 py-5 border-b border-white/5">
          <BrandMark />
        </Link>
        <div className="px-4 py-3 border-b border-white/5">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Түрээслэгч</div>
          <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-tenant-name">{tenant?.company_name || "—"}</div>
          <div className="font-mono text-xs text-magenta mt-1">{tenant?.phone_number || ""}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={link} data-testid={it.testid}>
              <it.icon className="h-4 w-4" />
              <span>{t(`sidebar.${it.key}`)}</span>
            </NavLink>
          ))}
          {user?.role === "super_admin" && (
            <NavLink to="/admin/tenants" className={link} data-testid="side-admin">
              <ShieldCheck className="h-4 w-4" /> <span>{t("sidebar.admin")}</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-2">
          <LanguageToggle className="w-full justify-center" />
          <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition px-4 py-2.5 rounded-xl border border-white/5 hover:border-white/10" data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> {t("nav.logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 grain relative">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 glass border-b border-white/10 sticky top-0 z-40">
          <Link to="/">
            <BrandMark size="sm" />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button onClick={onLogout} className="p-2 rounded-lg border border-white/5 text-slate-400" data-testid="logout-btn-mobile"><LogOut className="h-4 w-4"/></button>
          </div>
        </div>
        <div className="lg:hidden flex flex-wrap gap-1 p-2 border-b border-white/5 bg-bg/95 sticky top-[57px] z-30">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={({isActive})=>`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs ${isActive?"bg-primary text-white":"text-slate-400"}`}>
              <it.icon className="h-3.5 w-3.5"/> {t(`sidebar.${it.key}`)}
            </NavLink>
          ))}
        </div>
        <div className="p-6 lg:p-10 relative z-10">{children}</div>
      </main>
    </div>
  );
}
